import { appConfig } from '@common-config/app.config';
import type { StartTrialCardCaptureResponse, TrialCardQuote, TrialUpgradePromptResponse } from '@extension/wa-communication-layer/api/api-calls';

/** The existing checkout page (plan picker + hosted checkout). The popup never builds its own flow (#1228/#1229). */
export function trialUpgradeCheckoutUrl(plan: 'pro' | 'basic'): string {
  return `${appConfig.PRICING_PAGE}/account-subscription?plan=${plan}`;
}

/** True only when the server returned a complete, shown popup. */
export function shouldShowTrialUpgradePrompt(res: TrialUpgradePromptResponse | null | undefined): res is TrialUpgradePromptResponse & {
  type: NonNullable<TrialUpgradePromptResponse['type']>;
  plan: NonNullable<TrialUpgradePromptResponse['plan']>;
  copy: NonNullable<TrialUpgradePromptResponse['copy']>;
} {
  return !!res && res.show === true && res.type !== null && res.plan !== null && res.copy !== null;
}

/** Where Chargebee sends the customer back to: a page inside AppView, carrying the return marker. */
export function trialCardReturnUrl(): string {
  return `${appConfig.PRICING_PAGE}/account-subscription?trialCard=return`;
}

/** What the consent screen does with the server's answer to "start". */
export type StartStep =
  | { kind: 'navigate'; url: string }
  | { kind: 'reshow'; quote: TrialCardQuote }
  | { kind: 'fallback' };

export function nextStepAfterStart(res: StartTrialCardCaptureResponse): StartStep {
  if (res.kind === 'ok' && 'url' in res) return { kind: 'navigate', url: res.url };
  if (res.kind === 'quote_changed' && 'copy' in res) return { kind: 'reshow', quote: res };
  return { kind: 'fallback' };
}

export interface PendingTab {
  navigate(url: string): void;
  close(): void;
}

/**
 * Opens a blank tab. Call it SYNCHRONOUSLY inside the click handler: a tab opened after an async call is
 * popup-blocked. If the browser still blocks it, navigate() falls back to this tab, so the customer is never stranded.
 */
export interface TabLike { opener: unknown; location: { href: string }; close(): void }

export function openPendingTab(
  open: () => TabLike | null = () => window.open('about:blank', '_blank'),
  assign: (url: string) => void = (url) => window.location.assign(url),
): PendingTab {
  const tab = open();
  if (tab) tab.opener = null;
  return {
    navigate: (url) => { if (tab) tab.location.href = url; else assign(url); },
    close: () => { if (tab) tab.close(); },
  };
}
